import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import Datatable from "../../../core/common/dataTable";
import { ColumnsType } from "antd/es/table";
import { getSearchableHeader } from "../../utils/getSearchableHeader";
import Footer from "../../../core/common/Footer";
import useApi from "../../../core/customhook/useApi";
import { useAuth } from "../../../core/context/useAuthContext";
import { displayValue, formatDate } from "../../../core/common/TabelValue";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { filterByColumnSearch } from "../../utils/filterByColumnSearch";
import { safeStringSort } from "../../../core/methods/sorterMethods";

const ManageAccounts = () => {
  const [itemList, setItemList] = useState<any[]>([]);
  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});
  const [pagination, setPagination] = useState({ page: 1, limit: 1000 });
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const { auth } = useAuth();
  const orgId = auth?.orgId;

  const getAccounts = useApi<any>(
    `/api/v1/master/organizations/${orgId}/all-projects?page=${pagination.page}&limit=${pagination.limit}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const accountLoading = getAccounts.loading;
  const filteredData = filterByColumnSearch(
    Array.isArray(itemList) ? itemList : [],
    inventoryColumnSearch,
    {
      assisted_checks: (r) =>
        `${r.verified_assisted_checks ?? 0} / ${r.assisted_checks ?? 0}`,
      non_assisted_checks: (r) =>
        `${r.verified_non_assisted_checks ?? 0} / ${r.non_assisted_checks ?? 0}`,
    },
  );

  const getAccountList = async () => {
    if (!orgId) return;
    try {
      const res = await getAccounts.callApi({
        url: `/api/v1/master/organizations/${orgId}/all-projects?page=${pagination.page}&limit=${pagination.limit}`,
      });
      if (res?.success) {
        setItemList(res.data?.table || []);
        setTotal(res.data?.pagination?.total ?? 0);
      }
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    }
  };

  const handleTableChange = (
    page: number,
    limit: number = pagination.limit,
  ) => {
    if (page === pagination.page && limit === pagination.limit) return;
    const nextPage = limit !== pagination.limit ? 1 : page;
    setPagination({ page: nextPage, limit });
  };

  // re-fetch whenever org, page or page size changes
  useEffect(() => {
    getAccountList();
  }, [orgId, pagination.page, pagination.limit]);

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) =>
        (pagination.page - 1) * pagination.limit + index + 1,
    },

    {
      title: getSearchableHeader(
        "Account",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      sorter: (a, b) => safeStringSort(a.project_name, b.project_name),
      render: displayValue,
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: getSearchableHeader(
        "Code",
        "project_code",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_code",
      key: "project_code",
      sorter: (a, b) => safeStringSort(a.project_code, b.project_code),
      render: displayValue,
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: getSearchableHeader(
        "Manager Name",
        "accmanager_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "accmanager_name",
      key: "accmanager_name",
      sorter: (a, b) => safeStringSort(a.accmanager_name, b.accmanager_name),
      render: displayValue,
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: getSearchableHeader(
        "Email",
        "accmanager_email",
        setInventoryColumnSearch,
      ),
      dataIndex: "accmanager_email",
      key: "accmanager_email",
      sorter: (a, b) => safeStringSort(a.accmanager_email, b.accmanager_email),
      render: displayValue,
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: getSearchableHeader(
        "Created On",
        "created_at",
        setInventoryColumnSearch,
      ),
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => safeStringSort(a.created_at, b.created_at),
      render: (value) => formatDate(value),
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: getSearchableHeader(
        "Last Update",
        "updated_at",
        setInventoryColumnSearch,
      ),
      dataIndex: "updated_at",
      key: "updated_at",
      sorter: (a, b) => safeStringSort(a.updated_at, b.updated_at),
      render: (value) => formatDate(value),
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },

    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_: any, record: any) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() =>
            navigate(all_routes.updateAccount, {
              state: {
                projectId: record.project_id,
              },
            })
          }
        >
          <i className="ti ti-edit"></i>
        </button>
      ),
    },
    {
      title: "Active/Inactive",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (_: any, record: any) => (
        <div className="form-check form-switch d-flex justify-content-center">
          <input
            className="form-check-input"
            type="checkbox"
            checked={record.is_active}
            disabled
            // onChange={() => handleStatusToggle(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Manage Account</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Management</li>
                <li className="breadcrumb-item active">Manage Account</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>
        {accountLoading ? (
          <SnipperLoader />
        ) : (
          <div className="card mt-3">
            {/* <Datatable
              dataSource={filteredData}
              columns={columns}
              Selection={false}
              pagination={{
                current: pagination.page,
                pageSize: pagination.limit,
                total,
              }}
              handleTableChange={(pagination: any) => {
                handleTableChange(pagination.current, pagination.pageSize);
              }}
            /> */}
            <Datatable
              dataSource={filteredData}
              fixedHeader
              tableHeight={650}
              columns={columns}
              Selection={false}
              pagination={{
                current: pagination.page,
                pageSize: pagination.limit,
                total,
              }}
              handleTableChange={(p: any) =>
                handleTableChange(p.current, p.pageSize)
              }
            />
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ManageAccounts;
